export interface CustomizationOption {
  value: string;
  label: string;
  helperText?: string;
}

export interface CustomizationGroup {
  id: string;
  label: string;
  options: CustomizationOption[];
  /**
   * Optional default value; if not provided the second option will be used,
   * then the first as a fallback.
   */
  defaultValue?: string;
}

// Human‑readable labels for displaying customizations in the cart / orders
export const CUSTOMIZATION_LABELS: Record<string, string> = {
  size: 'Cup size',
  milk: 'Milk',
  shots: 'Espresso shots',
  sweetness: 'Sweetness',
  ice: 'Ice level',
  topping: 'Topping',
};

// Starbucks‑style but QahwaHub‑branded options
const SIZE_GROUP: CustomizationGroup = {
  id: 'size',
  label: 'Cup size',
  options: [
    { value: 'study-small', label: 'Study Cup (Small)' },
    { value: 'daily-medium', label: 'Daily Cup (Medium)', helperText: 'Our default size' },
    { value: 'gather-large', label: 'Gathering Cup (Large)' },
  ],
  defaultValue: 'daily-medium',
};

const MILK_GROUP: CustomizationGroup = {
  id: 'milk',
  label: 'Milk',
  options: [
    { value: 'no-milk', label: 'No milk' },
    { value: 'whole', label: 'Fresh Whole Milk' },
    { value: 'low-fat', label: 'Low‑fat Milk' },
    { value: 'lactose-free', label: 'Lactose‑free Milk' },
    { value: 'oat', label: 'Oat Milk' },
    { value: 'almond', label: 'Almond Milk' },
    { value: 'soy', label: 'Soy Milk' },
  ],
  defaultValue: 'whole',
};

const SHOTS_GROUP: CustomizationGroup = {
  id: 'shots',
  label: 'Espresso shots',
  options: [
    { value: '1', label: 'Single' },
    { value: '2', label: 'Double (House standard)' },
    { value: '3', label: 'Triple' },
  ],
  defaultValue: '2',
};

const SWEETNESS_GROUP: CustomizationGroup = {
  id: 'sweetness',
  label: 'Sweetness',
  options: [
    { value: 'no-sugar', label: 'No sugar' },
    { value: 'less-sweet', label: 'Less sweet' },
    { value: 'regular', label: 'QahwaHub regular' },
    { value: 'extra-sweet', label: 'Extra sweet' },
  ],
  defaultValue: 'regular',
};

const ICE_GROUP: CustomizationGroup = {
  id: 'ice',
  label: 'Ice level',
  options: [
    { value: 'no-ice', label: 'No ice' },
    { value: 'light-ice', label: 'Light ice' },
    { value: 'regular-ice', label: 'Regular ice' },
    { value: 'extra-ice', label: 'Extra ice' },
  ],
  defaultValue: 'regular-ice',
};

const TOPPING_GROUP: CustomizationGroup = {
  id: 'topping',
  label: 'Topping',
  options: [
    { value: 'none', label: 'No topping' },
    { value: 'cocoa', label: 'Cocoa dust' },
    { value: 'cinnamon', label: 'Cinnamon' },
    { value: 'caramel-drizzle', label: 'Caramel drizzle' },
    { value: 'chocolate-drizzle', label: 'Chocolate drizzle' },
    { value: 'whipped-cream', label: 'Whipped cream' },
  ],
  defaultValue: 'none',
};

/** Every option group customers can see — baristas pick which apply per menu item. */
export const ALL_CUSTOMIZATION_GROUPS: CustomizationGroup[] = [
  SIZE_GROUP,
  MILK_GROUP,
  SHOTS_GROUP,
  SWEETNESS_GROUP,
  ICE_GROUP,
  TOPPING_GROUP,
];

const GROUP_BY_ID = Object.fromEntries(
  ALL_CUSTOMIZATION_GROUPS.map((g) => [g.id, g])
) as Record<string, CustomizationGroup>;

export type CustomizationGroupId = (typeof ALL_CUSTOMIZATION_GROUPS)[number]['id'];

/** Per menu item: group id → option values the store offers for that drink. */
export type MenuItemCustomizationConfig = Partial<Record<CustomizationGroupId, string[]>>;

function parseStoredConfig(
  raw:
    | MenuItemCustomizationConfig
    | string[]
    | string
    | null
    | undefined
): MenuItemCustomizationConfig | null {
  if (raw == null) return null;

  let parsed: unknown = raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (Array.isArray(parsed)) {
    const config: MenuItemCustomizationConfig = {};
    for (const id of parsed) {
      const gid = String(id || '').trim() as CustomizationGroupId;
      const group = GROUP_BY_ID[gid];
      if (group) {
        config[gid] = group.options.map((o) => o.value);
      }
    }
    return Object.keys(config).length ? config : null;
  }

  if (parsed && typeof parsed === 'object') {
    const config: MenuItemCustomizationConfig = {};
    for (const [gid, values] of Object.entries(parsed as Record<string, string[]>)) {
      const group = GROUP_BY_ID[gid];
      if (!group || !Array.isArray(values)) continue;
      const allowed = new Set(group.options.map((o) => o.value));
      const filtered = values.filter((v) => allowed.has(v));
      const unique = Array.from(new Set(filtered));
      if (unique.length) config[gid as CustomizationGroupId] = unique;
    }
    return Object.keys(config).length ? config : null;
  }

  return null;
}

function applyConfigToGroups(config: MenuItemCustomizationConfig): CustomizationGroup[] {
  const groups: CustomizationGroup[] = [];
  for (const group of ALL_CUSTOMIZATION_GROUPS) {
    const enabled = config[group.id as CustomizationGroupId];
    if (!enabled?.length) continue;
    const allowed = new Set(enabled);
    const options = group.options.filter((o) => allowed.has(o.value));
    if (options.length) {
      groups.push({
        ...group,
        options,
        defaultValue:
          group.defaultValue && allowed.has(group.defaultValue)
            ? group.defaultValue
            : options[0]?.value,
      });
    }
  }
  return groups;
}

/** Groups + filtered options for customers (barista config, else heuristics). */
export function resolveCustomizationGroups(item: {
  category: string;
  name: string;
  customization_config?: MenuItemCustomizationConfig | string | null;
  customization_groups?: MenuItemCustomizationConfig | string[] | string | null;
}): CustomizationGroup[] {
  const config =
    parseStoredConfig(item.customization_config) ||
    parseStoredConfig(item.customization_groups);
  if (config) {
    return applyConfigToGroups(config);
  }
  return getCustomizationsForMenuItem(item);
}

export function defaultCustomizationValues(
  groups: CustomizationGroup[]
): Record<string, string> {
  const initial: Record<string, string> = {};
  groups.forEach((group) => {
    const explicitDefault = group.defaultValue;
    const fallback = group.options[0]?.value || '';
    const value =
      explicitDefault && group.options.some((o) => o.value === explicitDefault)
        ? explicitDefault
        : fallback;
    if (value) initial[group.id] = value;
  });
  return initial;
}

/** Full config with every suggested option checked (barista can uncheck). */
export function suggestCustomizationConfig(item: {
  category: string;
  name: string;
}): MenuItemCustomizationConfig {
  const config: MenuItemCustomizationConfig = {};
  for (const group of getCustomizationsForMenuItem(item)) {
    config[group.id as CustomizationGroupId] = group.options.map((o) => o.value);
  }
  return config;
}

/** @deprecated use suggestCustomizationConfig */
export function suggestCustomizationGroupIds(item: {
  category: string;
  name: string;
}): CustomizationGroupId[] {
  return Object.keys(suggestCustomizationConfig(item)) as CustomizationGroupId[];
}

export function formatCustomizationConfigSummary(
  config: MenuItemCustomizationConfig | null | undefined
): string {
  if (!config || !Object.keys(config).length) return 'Auto (name/category)';
  return Object.entries(config)
    .map(([gid, opts]) => {
      const label = CUSTOMIZATION_LABELS[gid] || gid;
      return `${label} (${opts?.length ?? 0})`;
    })
    .join(', ');
}

export function configFromApiItem(item: {
  customization_config?: MenuItemCustomizationConfig | null;
  customization_groups?: string[] | MenuItemCustomizationConfig | null;
}): MenuItemCustomizationConfig {
  return (
    parseStoredConfig(item.customization_config) ||
    parseStoredConfig(item.customization_groups) ||
    {}
  );
}

// Helper to decide which customizations apply to a given drink (legacy / auto-suggest)
export function getCustomizationsForMenuItem(item: { category: string; name: string }): CustomizationGroup[] {
  const category = (item.category || '').toLowerCase();
  const name = (item.name || '').toLowerCase();

  const isCoffeeDrink =
    category.includes('coffee') ||
    category.includes('espresso') ||
    category.includes('hot coffee') ||
    category.includes('signature drinks') ||
    name.includes('latte') ||
    name.includes('cappuccino') ||
    name.includes('mocha') ||
    name.includes('macchiato') ||
    name.includes('flat white');

  const isTeaLatte =
    category.includes('tea lattes') ||
    (category.includes('tea') && name.includes('latte'));

  const isColdOrIced =
    category.includes('cold') ||
    category.includes('iced') ||
    name.includes('iced') ||
    name.includes('frapp') ||
    name.includes('smoothie');

  const isJuiceOrSmoothie =
    category.includes('fresh juice') ||
    category.includes('smoothies');

  const groups: CustomizationGroup[] = [];

  // Sizes for all crafted drinks
  if (isCoffeeDrink || isTeaLatte || isColdOrIced || isJuiceOrSmoothie) {
    groups.push(SIZE_GROUP);
  }

  // Milk for milk‑based drinks
  if (isCoffeeDrink || isTeaLatte || name.includes('latte')) {
    groups.push(MILK_GROUP);
  }

  // Shots mainly for espresso drinks
  if (isCoffeeDrink || name.includes('espresso')) {
    groups.push(SHOTS_GROUP);
  }

  // Sweetness for almost all beverages
  if (isCoffeeDrink || isTeaLatte || isColdOrIced || isJuiceOrSmoothie || category.includes('tea')) {
    groups.push(SWEETNESS_GROUP);
  }

  // Ice for cold / iced drinks, juices, smoothies
  if (isColdOrIced || isJuiceOrSmoothie) {
    groups.push(ICE_GROUP);
  }

  // Toppings for espresso + tea lattes
  if (isCoffeeDrink || isTeaLatte) {
    groups.push(TOPPING_GROUP);
  }

  return groups;
}

// Price adjustments for customizations (frontend version).
// NOTE: The backend (server/routes/orders.js) contains a matching implementation
// to ensure totals cannot be manipulated from the client.
export function getPriceDeltaForCustomizations(
  item: { category: string; name: string },
  customizations?: Record<string, string>
): number {
  if (!customizations) return 0;

  const category = (item.category || '').toLowerCase();
  const name = (item.name || '').toLowerCase();

  const isDrinkCategory =
    category.includes('coffee') ||
    category.includes('espresso') ||
    category.includes('drink') ||
    category.includes('tea') ||
    category.includes('juice') ||
    category.includes('smoothie') ||
    name.includes('latte') ||
    name.includes('cappuccino') ||
    name.includes('mocha') ||
    name.includes('macchiato') ||
    name.includes('frapp') ||
    name.includes('iced');

  if (!isDrinkCategory) {
    return 0;
  }

  let delta = 0;

  // Sizes: only charge extra for the largest size, smaller stays at base price
  const size = customizations['size'];
  if (size === 'gather-large') {
    delta += 0.5; // +0.50 JOD for large
  }

  // Alternative milk upcharge (covers higher ingredient cost)
  const milk = customizations['milk'];
  if (milk === 'oat' || milk === 'almond' || milk === 'soy') {
    delta += 0.35; // +0.35 JOD for plant-based milks
  }

  // Extra shot upcharge
  const shots = customizations['shots'];
  if (shots === '3') {
    delta += 0.4; // +0.40 JOD for a third shot
  }

  // Premium toppings
  const topping = customizations['topping'];
  if (
    topping === 'whipped-cream' ||
    topping === 'caramel-drizzle' ||
    topping === 'chocolate-drizzle'
  ) {
    delta += 0.25; // +0.25 JOD for premium toppings
  }

  // Sweetness & ice levels are free customizations (no price impact)

  return delta;
}

